import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import CustomizedFundList from './CustomizedFundList';
import columnSchema from './columnSchema';
import rowGroupSchema from './rowGroupingSchema';
import filterSchema from './filterSchema';
import paginationSchema from './paginationSchema';
import watchListSchema from './watchListSchema';
import downloadSchema from './downloadSchema';
import downloadBookletSchema from './downloadBookletSchema';
import tableCntrlsPlaceholdersSchema from './tableCntrlsPlaceholdersSchema';
import tabsSchema from './tabsSchema';

const {
    schema: generatedSchema,
    uiSchema: generatedUiSchema,
    group: generatedGroup,
    dependencies: generatedDependencies,
} = customSchemas.GenerateAnalyticsSchema([
    'Download',
    'Text Filter',
    'Click Text Filter',
    'Dropdown Filter',
    'Date Filter',
    'Tab Selection',
    'Add Watchlist',
    'Remove Watchlist',
    'Watchlist Filter',
    'Compare',
    'Compare Button',
    'Active Compare Button',
    'Table Option Click',
    'Sort Filter',
]);

export const customizedFundListSchema = (props: any): any => {
    const schema: any = {
        title: 'Customized Fund List',
        type: 'object',
        required: [],
        properties: {
            styles: {
                type: 'string',
                description: 'A stylesheet',
                default: '',
            },
            ...customSchemas.textSchema.TextSchema.schema,
            ...customSchemas.styleSchema.StyleSchema.schema,
            ...customSchemas.layoutSchema.LayoutSchema.schema,
            ...generatedSchema,
            rows: {
                type: 'string',
                title: 'Enter rows data key',
                description: 'Enter json data key',
            },
            addPlaceholderWhenNoData: {
                type: 'boolean',
                title: 'Add placeholder when no data found',
                description: '',
            },
            mobileBreakpoint: {
                type: 'string',
                title: 'Mobile Breakpoint',
                description: 'Enter value',
            },
            disableTabStaticData: {
                type: 'boolean',
                title: 'Disable tabbing through static data',
                description: '',
            },
            disableLocalStorage: {
                type: 'boolean',
                title: 'Disable using local storage',
                description: '',
            },
            defaultSortId: {
                type: 'string',
                title: 'Default sort id to sort table',
                description: 'Enter value',
            },
            defaultSortingOrder: {
                type: 'string',
                title: 'Default sorting order',
                description: 'Select sorting state order',
                enum: ['ascending', 'descending'],
                default: 'ascending',
            },
            enableColumnToggling: {
                type: 'boolean',
                title: 'Enable column toggling',
                description: '',
            },
            enableColumnGrouping: {
                type: 'boolean',
                title: 'Enable column grouping',
                description: '',
            },
            moveControlsToTop: {
                type: 'boolean',
                title: 'Move controls to top',
                description: '',
            },
            ...rowGroupSchema.schema,
            ...tableCntrlsPlaceholdersSchema.schema,
            ...columnSchema.schema(props),
            ...paginationSchema.schema,
            ...filterSchema.schema(props),
            ...watchListSchema.schema,
            ...downloadSchema.schema,
            ...downloadBookletSchema.schema,
            ...tabsSchema.schema(props),
        },
        dependencies: {
            ...customSchemas.layoutSchema.LayoutSchema.dependencies,
            ...paginationSchema.dependencies,
            ...watchListSchema.dependencies,
            ...downloadSchema.dependencies,
            ...downloadBookletSchema.dependencies,
            ...rowGroupSchema.dependencies,
            ...generatedDependencies,
            enableColumnToggling: {
                oneOf: [
                    {
                        properties: {
                            enableColumnToggling: { enum: [true] },
                            columnTogglingTitle: {
                                type: 'string',
                                title: 'Column toggling title',
                                description: 'Enter value',
                            },
                            columnTogglingSubtitle: {
                                type: 'string',
                                title: 'Column toggling subtitle',
                                description: 'Enter value',
                            },
                            columnTogglingSaveText: {
                                type: 'string',
                                title: 'Column toggling save text',
                                description: 'Enter value',
                            },
                            columnTogglingCancelText: {
                                type: 'string',
                                title: 'Column toggling cancel text',
                                description: 'Enter value',
                            },
                            columnTogglingSelectAllText: {
                                type: 'string',
                                title: 'Column toggling select all text',
                                description: 'Enter value',
                            },
                            columnTogglingDeselectAllText: {
                                type: 'string',
                                title: 'Column toggling deselect all text',
                                description: 'Enter value',
                            },
                        },
                    },
                    {
                        properties: {
                            enableColumnToggling: { enum: [false] },
                        },
                    },
                ],
            },
            enableColumnGrouping: {
                oneOf: [
                    {
                        properties: {
                            enableColumnGrouping: { enum: [true] },
                            columnGroups: {
                                type: 'array',
                                title: 'Add Columns groups',
                                items: {
                                    type: 'object',
                                    required: ['id', 'label'],
                                    properties: {
                                        id: {
                                            type: 'string',
                                            title: 'Enter property Id',
                                        },
                                        label: {
                                            type: 'string',
                                            title: 'Enter column label',
                                        },
                                    },
                                },
                            },
                        },
                    },
                    {
                        properties: {
                            enableColumnGrouping: { enum: [false] },
                        },
                    },
                ],
            },
        },
    };

    return schema;
};

export const customizedFundListSchemaGroups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        'Table Options': [
            'columns',
            'rows',
            'isAddPlaceholderInLeftPanelOfTableCtrls',
            'isAddPlaceholderInRightPanelOfTableCtrls',
            'isAddPlaceholderInBottomPanelOfTableCtrls',
            'addPlaceholderWhenNoData',
            'mobileBreakpoint',
            'disableLocalStorage',
            'defaultSortId',
            'defaultSortingOrder',
            'enableColumnGrouping',
            'moveControlsToTop',
            'columnGroups',
            'enableColumnToggling',
            'columnTogglingTitle',
            'columnTogglingSubtitle',
            'columnTogglingSaveText',
            'columnTogglingCancelText',
            'columnTogglingSelectAllText',
            'columnTogglingDeselectAllText',
        ],
        Tabs: ['tabs', 'tabUrlParam', 'hideTabs', 'isTabsSticky'],
        Pagination: [
            'enablePagination',
            'pagingType',
            'pagerButtonStyle',
            'pagePickerPosition',
            'pagePickerDropdownImage',
            'defaultPageSize',
            'showPageSizePicker',
            'enableShowAll',
            'showAllText',
            'configurePageSizes',
            'pageSizePickerPosition',
            'pageSizePickerText',
            'showCurrentPageText',
            'currentPageTextPosition',
            'currentPageText',
            'previousBtnText',
            'nextBtnText',
            'firstBtnText',
            'lastBtnText',
        ],
        'Row Grouping': [
            'enableGrouping',
            'groupingPropertyId',
            'showGroupingCount',
            'isCollapseRowGroup',
            'showRowGroupIconName',
            'hideRowGroupIconName',
        ],
        Watchlist: [
            'enableWatchlist',
            'watchlistStorageName',
            'watchlistDataId',
            'watchlistButtonText',
            'showAllFundsBtnText',
            'addToWatchlistText',
            'removeFromWatchlistText',
        ],
        'Filters Options': ['filters', 'filtersUI'],
        Download: [
            'enableDownload',
            'downloadFileType',
            'downloadButtonText',
            'downloadFileName',
            'downloadFileTabName',
            'fileNameBasedOnTab',
            'tabPreDescription',
            'tabDescription',
            'showDownloadAtBottom',
        ],
        DownloadBooklet: [
            'downloadBooklet',
            'downloadBookletButtonText',
            'showDownloadBookletIcon',
            'downloadBookletIconType',
            'downloadBookletIconPosition',
            'downloadBookletIconName',
            'downloadBookletImage',
            'downloadBookletMessage',
            'downloadBookletMessageIcon',
            'downloadBookletMessageIconType',
            'downloadBookletMessageIconName',
            'downloadBookletMessageImage',
            'downloadBookletMessageIconPosition',
            'downloadBookletErrorMessage',
            'downloadBookletErrorMessageIcon',
            'downloadBookletErrorMessageIconType',
            'downloadBookletErrorMessageIconPosition',
            'downloadBookletErrorMessageIconName',
            'downloadBookletErrorMessageImage',
            'downloadLinkText',
            'downloadBookletText',
            'bookletName',
        ],
        ...generatedGroup,
        Accessibility: ['disableTabStaticData'],
    },
];

const CustomizedFundListWidget: commonTypes.IWidget = {
    schema: (props: any): JSONSchema7 => customizedFundListSchema(props),
    uiSchema: {
        'ui:groups': customizedFundListSchemaGroups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        rows: { 'ui:widget': 'CustomTextInput' },
        mobileBreakpoint: { 'ui:widget': 'UnitSelectorWidget' },
        disableLocalStorage: { 'ui:widget': 'CustomCheckbox' },
        defaultSortId: { 'ui:widget': 'CustomTextInput' },
        addPlaceholderWhenNoData: { 'ui:widget': 'CustomCheckbox' },
        defaultSortingOrder: { 'ui:widget': 'CustomSelect' },
        enableColumnToggling: { 'ui:widget': 'CustomCheckbox' },
        enableColumnGrouping: { 'ui:widget': 'CustomCheckbox' },
        moveControlsToTop: { 'ui:widget': 'CustomCheckbox' },
        columnTogglingTitle: { 'ui:widget': 'ResourcePicker' },
        columnTogglingSubtitle: { 'ui:widget': 'ResourcePicker' },
        columnTogglingSaveText: { 'ui:widget': 'ResourcePicker' },
        columnTogglingCancelText: { 'ui:widget': 'ResourcePicker' },
        columnTogglingSelectAllText: { 'ui:widget': 'ResourcePicker' },
        columnTogglingDeselectAllText: { 'ui:widget': 'ResourcePicker' },
        ...rowGroupSchema.uiSchema,
        ...columnSchema.uiSchema,
        ...paginationSchema.uiSchema,
        ...filterSchema.uiSchema,
        ...watchListSchema.uiSchema,
        ...downloadSchema.uiSchema,
        ...downloadBookletSchema.uiSchema,
        ...tableCntrlsPlaceholdersSchema.uiSchema,
        ...tabsSchema.uiSchema,
        ...generatedUiSchema,
        columnGroups: {
            items: {
                id: { 'ui:widget': 'CustomTextInput' },
                label: { 'ui:widget': 'CustomTextInput' },
            },
        },
        disableTabStaticData: { 'ui:widget': 'CustomCheckbox' },
    },
    templateView: CustomizedFundList,
    id: 'CustomizedFundList',
    group: 'component',
    contentPlaceHolders: ['children'],
    initialData: () => ({}),
};

export default CustomizedFundListWidget;
